import { useState } from 'react';

import type { TicketDetail, TicketDetailReview, TicketDetailReviewFindingRecord } from '@nightshift/ui-types';

import { Empty } from '../../components/primitives.tsx';
import { useTimeFormat } from '../../lib/display.ts';
import { compactDuration, observedTime, usd } from '../../lib/format.ts';
import { groupReviewWaves, reviewVerdictLabel, type ReviewWave } from '../../lib/review-waves.ts';
import { RunTranscript } from './run-transcript.tsx';
import './review.css';

/**
 * Review tab: every reviewer verdict on this ticket, newest wave open, older waves folded.
 * The verdict text comes from the reviewer's stored canonical result, so this is what the
 * reviewer actually said, not a paraphrase. A wave that judged an older head is marked
 * stale rather than hidden.
 */
/** Findings below the project's `review.findings_shown` level are counted, never silently dropped. */
function hiddenFindingsNote(count: number): string {
  return `${count} lower-severity ${count === 1 ? 'finding' : 'findings'} hidden by the project's review setting.`;
}

export function ReviewTab({ detail, now }: { detail: TicketDetail; now: number }) {
  if (detail.reviews.length === 0) {
    return (
      <Empty
        title="No AI review yet."
        body="Reviewer verdicts appear here once the ticket reaches AI Review. Each reviewer's summary, findings and reviewed files are shown as it wrote them."
      />
    );
  }
  const waves = groupReviewWaves(detail.reviews);
  return (
    <div className="review-ws">
      <FindingHistory history={detail.reviewHistory} />
      {waves.map((wave, index) => (
        <ReviewWaveSection key={wave.sha ?? `unpinned-${index}`} wave={wave} now={now} open={index === 0} />
      ))}
    </div>
  );
}

const STATE_LABEL: Record<TicketDetailReviewFindingRecord['state'], string> = {
  introduced: 'new',
  unresolved: 'still present',
  resolved: 'resolved',
  regressed: 'regression',
};

/**
 * Every finding across rounds by its stable id, with the state the stored verdicts give it.
 * A regression is a finding that was resolved in one round and reported again later; it is
 * named as such here and in the implementer's bounce so nobody reads it as a new objection.
 */
export function FindingHistory({ history }: { history: TicketDetailReviewFindingRecord[] }) {
  if (history.length === 0) return null;
  const open = history.filter((row) => row.state !== 'resolved');
  const resolved = history.length - open.length;
  return (
    <details className="review-history" open={open.some((row) => row.state === 'regressed' || row.streak >= 2)}>
      <summary className="review-wave-head">
        <span className="drawer-section-kicker review-wave-kicker">Findings across rounds</span>
        <span className="dim review-wave-count">
          {open.length} open · {resolved} resolved
        </span>
      </summary>
      <ul className="review-findings review-history-list">
        {history.map((row) => (
          <li key={row.id} className={`review-finding is-${row.severity} state-${row.state}`} data-state={row.state}>
            <code className="review-finding-id">{row.id}</code>
            <span className={`review-finding-state is-${row.state}`}>
              {STATE_LABEL[row.state]}
              {row.state === 'unresolved' && row.streak >= 2 ? ` ×${row.streak}` : ''}
            </span>
            {row.file ? (
              <code className="review-location">
                {row.file}
                {row.line != null ? `:${row.line}` : ''}
              </code>
            ) : null}
            <span className="review-finding-text">{row.description}</span>
            {row.cite ? <span className="dim review-finding-cite">{row.cite}</span> : null}
          </li>
        ))}
      </ul>
    </details>
  );
}

export function ReviewWaveSection({
  wave,
  now,
  open,
  compact = false,
}: {
  wave: ReviewWave;
  now: number;
  open: boolean;
  compact?: boolean;
}) {
  const heading = wave.sha ? `Head ${wave.sha.slice(0, 8)}` : 'No head pinned';
  return (
    <details className={wave.stale ? 'review-wave is-stale' : 'review-wave'} open={open}>
      <summary className="review-wave-head">
        <span className="drawer-section-kicker review-wave-kicker">{heading}</span>
        {wave.stale ? <span className="drawer-stale-mark">Stale — reviewed an older MR head</span> : null}
        <span className="dim review-wave-count">
          {wave.reviews.length} {wave.reviews.length === 1 ? 'reviewer' : 'reviewers'}
        </span>
      </summary>
      <div className="review-wave-body">
        {wave.reviews.map((review) => (
          <ReviewCard key={review.attemptId} review={review} now={now} compact={compact} />
        ))}
      </div>
    </details>
  );
}

const VERDICT_CLASS: Record<ReturnType<typeof reviewVerdictLabel>, string> = {
  Pass: 'is-pass',
  'Pass — verification gaps': 'is-pass-with-gaps',
  Fail: 'is-fail',
  Running: 'is-running',
  Malformed: 'is-malformed',
  Cancelled: 'is-cancelled',
};

export function ReviewCard({
  review,
  now,
  compact = false,
}: {
  review: TicketDetailReview;
  now: number;
  compact?: boolean;
}) {
  const format = useTimeFormat();
  const [showTranscript, setShowTranscript] = useState(false);
  const label = reviewVerdictLabel(review);
  const model = [review.harness, review.model].filter(Boolean).join(' / ');
  const when = review.finishedAt ?? review.startedAt;

  return (
    <article
      id={`review-attempt-${review.attemptId}`}
      className={`review-card ${VERDICT_CLASS[label]}`}
      data-attempt={review.attemptId}
    >
      <header className="review-card-head">
        <span className={`review-verdict ${VERDICT_CLASS[label]}`}>{label}</span>
        <strong className="review-card-name">{review.reviewerName ?? `attempt ${review.ordinal}`}</strong>
        <span className="review-card-meta dim">
          {model ? <span>{model}</span> : null}
          {review.machine ? <span>on {review.machine}</span> : null}
          {review.durationMs != null ? <span className="num">{compactDuration(review.durationMs)}</span> : null}
          {review.costUsd != null ? <span className="num">{usd(review.costUsd)}</span> : null}
          {when != null ? (
            <time className="num" dateTime={new Date(when).toISOString()}>
              {observedTime(when, now, format)}
            </time>
          ) : null}
        </span>
      </header>

      {review.malformed ? <p className="review-card-malformed">{review.malformed}</p> : null}

      {review.summary ? (
        <p className="review-card-summary">{review.summary}</p>
      ) : label === 'Running' ? (
        <p className="review-card-summary dim">Still reviewing.</p>
      ) : null}

      {label === 'Pass — verification gaps' ? (
        <section className="review-card-section review-approval-rationale">
          <h4 className="review-card-kicker">Why approval is reasonable</h4>
          <p>
            {review.approvalRationale ??
              'The reviewer approved this change without explaining why the unreported or unresolved assessments were acceptable.'}
          </p>
        </section>
      ) : null}

      {review.specDispute ? (
        <section className="review-card-section">
          <h4 className="review-card-kicker">Spec dispute</h4>
          {review.disputes.map((dispute, index) => (
            <blockquote key={index} className="review-dispute">
              <p className="review-dispute-criterion">{dispute.criterion}</p>
              <p>{dispute.problem}</p>
            </blockquote>
          ))}
        </section>
      ) : null}

      {review.criteria.length > 0 ? (
        <section className="review-card-section">
          <h4 className="review-card-kicker">
            {review.coverage.structuredReported === false ? 'Structured assessment not reported' : `Assessments reported · ${review.coverage.reported} of ${review.coverage.total}`}
          </h4>
          <ul className="review-coverage">
            {review.criteria.map((row, index) => (
              <li key={`${row.index}-${index}`} className={`review-coverage-row is-${row.status}`} data-status={row.status}>
                <span className={`review-coverage-status is-${row.status}`}>
                  {{
                    met: 'Met',
                    'not-met': 'Not met',
                    unproven: 'Unable to verify',
                    'not-checked': 'Not checked this round',
                    'not-assessed': 'Assessment not reported',
                  }[row.status]}
                </span>
                <span className="review-coverage-text">
                  {row.index > 0 ? `AC ${row.index}: ` : ''}
                  {row.criterion}
                </span>
                {row.priorAttemptId ? (
                  <a className="review-coverage-source" href={`#review-attempt-${row.priorAttemptId}`}>
                    Prior assessment #{row.priorAttemptId}
                  </a>
                ) : null}
                {row.note ? <span className="dim review-coverage-note">{row.note}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : review.verdict !== null && review.coverage.total > 0 ? (
        <p className="review-card-none dim">No criterion assessments were reported.</p>
      ) : null}

      {review.findings.length > 0 ? (
        <section className="review-card-section">
          <h4 className="review-card-kicker">Findings</h4>
          <ul className="review-findings">
            {review.findings.map((finding, index) => (
              <li key={index} className={`review-finding is-${finding.severity}`} data-finding-id={finding.id}>
                <span className="review-severity">{finding.severity}</span>
                <code className="review-finding-id">{finding.id}</code>
                {finding.state !== 'introduced' ? (
                  <span className={`review-finding-state is-${finding.state}`}>{STATE_LABEL[finding.state]}</span>
                ) : null}
                {finding.file ? (
                  <code className="review-location">
                    {finding.file}
                    {finding.line != null ? `:${finding.line}` : ''}
                  </code>
                ) : null}
                <span className="review-finding-text">{finding.description}</span>
              </li>
            ))}
          </ul>
          {review.hiddenFindings > 0 ? <p className="review-card-none dim">{hiddenFindingsNote(review.hiddenFindings)}</p> : null}
        </section>
      ) : review.hiddenFindings > 0 ? (
        <p className="review-card-none dim">{hiddenFindingsNote(review.hiddenFindings)}</p>
      ) : review.verdict === 'pass' && !compact ? (
        <p className="review-card-none dim">No findings.</p>
      ) : null}

      {review.flaggedComments.length > 0 ? (
        <section className="review-card-section">
          <h4 className="review-card-kicker">Inline comments</h4>
          <ul className="review-findings">
            {review.flaggedComments.map((comment, index) => (
              <li key={index} className={comment.blocking ? 'review-finding is-error' : 'review-finding is-info'}>
                <span className="review-severity">{comment.blocking ? 'blocking' : 'note'}</span>
                <code className="review-location">
                  {comment.path}:{comment.line}
                </code>
                <span className="review-finding-text">{comment.body}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {!compact && review.reviewedFiles.length > 0 ? (
        <details className="review-files">
          <summary className="dim">
            Reviewed {review.reviewedFiles.length} {review.reviewedFiles.length === 1 ? 'file' : 'files'}
          </summary>
          <ul className="mono">
            {review.reviewedFiles.map((file) => (
              <li key={file}>{file}</li>
            ))}
          </ul>
        </details>
      ) : null}

      {!compact ? (
        <div className="review-card-foot">
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowTranscript((open) => !open)}>
            {showTranscript ? 'Hide transcript' : 'Transcript'}
          </button>
          {review.verdictArtifactId != null ? (
            <span className="dim review-card-artifact">verdict artifact #{review.verdictArtifactId}</span>
          ) : null}
        </div>
      ) : null}
      {showTranscript ? <RunTranscript runId={review.attemptId} maxHeight={280} /> : null}
    </article>
  );
}
